import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useLangStore } from '@app/dashboard/store/lang';
import * as amplitude from '@amplitude/analytics-browser';

import {
  Badge,
  PageStatus,
  SkeletonBase
} from '@unified-checkout/design-system';
import {
  CircleExclamation,
  MoneyBillTransfer,
  Pen,
  TrashIcon
} from '@unified-checkout/design-system/icons/index';

import { getSavedBillsDetails } from '@app/dashboard/services/inner_layout/bills/get-saved-bills.details';
import { getSavedBills } from '@app/dashboard/services/inner_layout/bills/get-saved-bills';
import { QUERY_KEYS } from '@app/dashboard/constants/query-keys';
import { BILLS_EVENTS, BILLS_EVENTS_PARAMS } from '@app/dashboard/constants/tracking_events/bills';

import { DeleteSavedBills } from './Modals/Delete';
import { UpdateSavedBillsModal } from './Modals/Update';
import { SavedBillsInquiryModal } from './Modals/Inquiry';

import styles from './saved-bills.module.scss';

export const SavedBills = ({ billsData }: { billsData: any }) => {
  const { t } = useTranslation();
  const { lang } = useLangStore();

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedBill, setSelectedBill] = useState<any>(null);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showInquiryModal, setShowInquiryModal] = useState(false);

  const {
    data: savedBillsData,
    isLoading: isSavedBillsLoading,
    error: savedBillsError,
  } = useQuery({
    queryKey: [QUERY_KEYS.GET_SAVED_BILLS, lang],
    queryFn: getSavedBills,
  });

  const {
    data: savedBillsDetailsData,
    isLoading: isSavedBillsDetailsLoading,
    isFetching: isSavedBillsDetailsFetching
  } = useQuery({
    queryKey: [QUERY_KEYS.GET_SAVED_BILLS_DETAILS, selectedBill?.id],
    queryFn: () => getSavedBillsDetails(selectedBill.id),
    enabled: !!selectedBill?.id && showInquiryModal,
  });

  const {
    data: savedBills,
  } = savedBillsData?.data || {};

  useEffect(() => {
    if (!isSavedBillsLoading) {
      amplitude.track(BILLS_EVENTS.VIEW_SAVED_BILLS, {
        [BILLS_EVENTS_PARAMS.ERROR]: (savedBillsError as any)?.response?.data?.message,
        [BILLS_EVENTS_PARAMS.STATUS]: savedBillsData?.data?.status,
      });
    }
  }, [isSavedBillsLoading]);


  const handleDeleteBill = (bill: any) => {
    setSelectedBill(bill);
    setShowDeleteModal(true);
  }

  const handleUpdateBill = (bill: any) => {
    setSelectedBill(bill);
    setShowUpdateModal(true);
  }

  const handleSavedBillClick = (bill: any) => {
    amplitude.track(BILLS_EVENTS.BILLS_SAVED_BILL_SELECTED, {
      [BILLS_EVENTS_PARAMS.BILL_CATEGORY]: bill?.category_name,
      [BILLS_EVENTS_PARAMS.BILL_SERVICE]: bill?.name,
      [BILLS_EVENTS_PARAMS.TOTAL_AMOUNT]: bill?.amount,
      [BILLS_EVENTS_PARAMS.TRANSACTION_ID]: bill?.id,
    });
    setSelectedBill(bill);
    setShowInquiryModal(true);
  }


  if (isSavedBillsLoading) {
    return (
      <div className={styles['saved-bills']}>
        {Array.from({ length: 8 }).map((_, index) => (
          <div key={index} className={styles['saved-bills__card']}>
            <SkeletonBase height="7rem" />
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className={styles['saved-bills']}>
      {!savedBills || savedBills.length === 0 ? (
        <div className={styles['saved-bills__empty']}>
          <PageStatus
            status="error"
            icon={CircleExclamation}
            title={t('innerLayout.billsPage.savedBills.noSavedBills')}
            description={t('innerLayout.billsPage.savedBills.noSavedBillsDescription')}
          />
        </div>
      ) : (
        savedBills.map((bill: any) => {
          return (
            <div
              key={bill.id}
              className={styles['saved-bills__card']}
              onClick={() => handleSavedBillClick(bill)}
            >
              <div className={styles['saved-bills__card__header']}>
                <div className={styles['saved-bills__card__header__content']}>
                  {
                    bill.category_icon ?
                      <img src={bill.category_icon} />
                      :
                      MoneyBillTransfer
                  }
                  <div className={styles['saved-bills__card__header__content__details']}>
                    <div className={styles['saved-bills__card__header__content__details__name']}>
                      {bill.name}
                    </div>
                    <div className={styles['saved-bills__card__header__content__details__number']}>
                      {bill.sub_title}
                    </div>
                  </div>
                </div>

                <div className={styles['saved-bills__card__header__buttons']}>
                  <div onClick={(e) => {
                    e.stopPropagation();
                    handleUpdateBill(bill)
                  }}>
                    {Pen}
                  </div>
                  <div onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteBill(bill)
                  }}>
                    {TrashIcon}
                  </div>
                </div>

              </div>
              <div className={styles['saved-bills__card__footer']}>
                <div className={styles['saved-bills__card__footer__content']}>
                  <div className={styles['saved-bills__card__footer__content__amount']}>
                    {bill.currency} {bill.amount}
                  </div>
                  <Badge text={bill.category_name} size='md' />
                </div>
              </div>
            </div>
          )
        }))}
      <DeleteSavedBills
        showDeleteModal={showDeleteModal}
        setShowDeleteModal={setShowDeleteModal}
        selectedBill={selectedBill}
      />
      <UpdateSavedBillsModal
        showUpdateModal={showUpdateModal}
        setShowUpdateModal={setShowUpdateModal}
        selectedBill={selectedBill}
      />
      <SavedBillsInquiryModal
        showInquiryModal={showInquiryModal}
        setShowInquiryModal={setShowInquiryModal}
        selectedBill={selectedBill}
        data={savedBillsDetailsData}
        billsData={billsData}
        isLoading={isSavedBillsDetailsLoading || isSavedBillsDetailsFetching}
      />
    </div>
  );
};